const GOOGLE_TRANSLATE_GUARD_FLAG = Symbol.for("videototextai.google-translate-guard");

type GuardNode = {
  parentNode: unknown;
};

type NodeMutationTarget = {
  removeChild(this: GuardNode, child: GuardNode): GuardNode;
  insertBefore(this: GuardNode, newNode: GuardNode, referenceNode: GuardNode | null): GuardNode;
  [GOOGLE_TRANSLATE_GUARD_FLAG]?: () => void;
};

function isNotFoundDomError(error: unknown): boolean {
  return error instanceof Error && error.name === "NotFoundError";
}

export function patchGoogleTranslateDomPrototype(target: NodeMutationTarget): () => void {
  const existingRestore = target[GOOGLE_TRANSLATE_GUARD_FLAG];

  if (existingRestore) {
    return existingRestore;
  }

  const originalRemoveChild = target.removeChild;
  const originalInsertBefore = target.insertBefore;

  target.removeChild = function patchedRemoveChild(this: GuardNode, child: GuardNode) {
    if (child.parentNode !== this) {
      return child;
    }

    try {
      return originalRemoveChild.call(this, child);
    } catch (error) {
      if (isNotFoundDomError(error)) {
        return child;
      }

      throw error;
    }
  };

  target.insertBefore = function patchedInsertBefore(this: GuardNode, newNode: GuardNode, referenceNode: GuardNode | null) {
    if (referenceNode !== null && referenceNode.parentNode !== this) {
      return originalInsertBefore.call(this, newNode, null);
    }

    try {
      return originalInsertBefore.call(this, newNode, referenceNode);
    } catch (error) {
      if (referenceNode !== null && isNotFoundDomError(error)) {
        return originalInsertBefore.call(this, newNode, null);
      }

      throw error;
    }
  };

  const restore = () => {
    if (target[GOOGLE_TRANSLATE_GUARD_FLAG] !== restore) {
      return;
    }

    target.removeChild = originalRemoveChild;
    target.insertBefore = originalInsertBefore;
    delete target[GOOGLE_TRANSLATE_GUARD_FLAG];
  };

  target[GOOGLE_TRANSLATE_GUARD_FLAG] = restore;

  return restore;
}

export function installGoogleTranslateDomGuard(): void {
  if (typeof Node === "undefined") {
    return;
  }

  patchGoogleTranslateDomPrototype(Node.prototype as NodeMutationTarget);
}
